import {
  faCoinFront,
  faLayerGroup,
  faSpinner,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Chip, Stack, SxProps, Tooltip, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";

import { Link, Typography } from "@/components/ui-v2";
import { useAccess, useBasicData } from "@/hooks";

const UnitsSection = styled(Box)<{ fullWidth?: boolean }>(({ theme }) => ({
  padding: theme.spacing(1),
}));

type UnitsEntitlementMessageType =
  | "insufficient-units"
  | "units-warning"
  | "info";

type UnitMessageData = {
  label: string;
  message?: string;
  textColor: string;
  chipTextColorLight: string;
  shouldShowDecimals?: boolean;
};

export interface UnitsContainerProps {
  units?: number | null;
  isLoading: boolean;
  isGated?: boolean;
  entityLabel?: string;
  sx?: SxProps;
  shouldShowDecimals?: boolean;
  resultsCount?: number;
}

const UNIT_MESSAGE_MAP: Record<UnitsEntitlementMessageType, UnitMessageData> = {
  "insufficient-units": {
    label: "Insufficient Units",
    message:
      "You are out of creative units, contact your administrator to add more.",
    textColor: "error.light",
    chipTextColorLight: "error.light",
  },
  "units-warning": {
    label: "Cost over 100 units",
    message: "This $action will use a high number of credits",
    textColor: "warning.light",
    chipTextColorLight: "warning.light",
  },
  info: {
    label: "Creative Units",
    textColor: "secondary.3",
    chipTextColorLight: "primary.light",
  },
};

export const UnitsContainer = (props: UnitsContainerProps) => {
  const {
    units,
    isLoading,
    isGated,
    entityLabel,
    sx,
    resultsCount,
    shouldShowDecimals = true,
  } = props;
  const theme = useTheme();
  const { hasPermission } = useAccess();
  const { workspace } = useBasicData();

  const unitsText = useMemo(() => {
    if (typeof units !== "number" || isNaN(units)) return "-";

    return shouldShowDecimals ? units.toFixed(1) : units.toFixed();
  }, [units, shouldShowDecimals]);

  const [messageType, setMessageType] =
    useState<UnitsEntitlementMessageType>("info");

  useEffect(() => {
    if (isGated) {
      setMessageType("insufficient-units");
    } else if (units && units > 0 && !isGated) {
      setMessageType("info");
    }
  }, [units, isGated]);

  const { textColor, label, chipTextColorLight } =
    UNIT_MESSAGE_MAP[messageType];

  const getMessage = useMemo(() => {
    const messageData = UNIT_MESSAGE_MAP[messageType];

    if (messageType === "insufficient-units") {
      if (hasPermission("workspace:billing:admin")) {
        return (
          <Typography
            fontWeight={200}
            color={messageData.textColor}
            variant="micro"
          >
            You are out of creative units, visit your{" "}
            <Link
              sx={{
                color: messageData.textColor,
                fontSize: 12,
                textDecoration: "underline",
                "&:hover": {
                  color: theme.palette.error.main,
                },
              }}
              href={`/${workspace?.name}/settings/usage`}
            >
              Billing & Usage
            </Link>{" "}
            page to add more.
          </Typography>
        );
      }
    }

    if (messageData.message) {
      const formattedMessage = messageData.message.replace(
        "$action",
        entityLabel ?? "forge",
      );
      return (
        <Typography fontWeight={300} variant="mini">
          {formattedMessage}
        </Typography>
      );
    }

    return null;
  }, [messageType, entityLabel, workspace?.name, hasPermission, theme]);

  const getTooltipMessage = () => {
    if (isLoading) {
      return "Loading creative units information...";
    }
    if (typeof units !== "number" || isNaN(units)) {
      return `A specific CU price is not currently available for this ${entityLabel ?? "action"}. Please visit your Usage page to see this ${entityLabel ?? "action"}'s price.`;
    }
    return `This ${entityLabel ?? "action"} will cost you ${unitsText} creative units.`;
  };

  return (
    <UnitsSection sx={{ color: textColor }}>
      <Stack direction="column">
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
          }}
        >
          <Typography fontWeight={500} variant="mini" sx={{ flexGrow: 1 }}>
            {label}
          </Typography>
          <Tooltip
            title={getTooltipMessage()}
            arrow
            placement="top"
            enterDelay={300}
          >
            <Chip
              label={
                isLoading ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    data-testid="credit-units-loading"
                  />
                ) : (
                  <Stack direction="row" alignItems="center" gap={2}>
                    {resultsCount != null && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <FontAwesomeIcon icon={faLayerGroup} />
                        {resultsCount}
                      </Stack>
                    )}
                    <Stack direction="row" alignItems="center" gap={1}>
                      <FontAwesomeIcon
                        icon={faCoinFront}
                        data-testid="credit-units-icon"
                      />
                      {unitsText}
                    </Stack>
                  </Stack>
                )
              }
              variant="filled"
              size="small"
              sx={{
                ...sx,
                color: chipTextColorLight,
                cursor: "default",
              }}
            />
          </Tooltip>
        </Stack>
        {getMessage}
      </Stack>
    </UnitsSection>
  );
};
